import type {
  Stripe,
  StripeCardElement,
  StripeElementChangeEvent,
} from '@stripe/stripe-js'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type AddCardBillingMethodFn } from '@/features/billing'
import { PaymentRequired } from 'fejl'
import { type Member } from '@/features/iam/members/Member'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { Id } from '@/infra/id'
import { extractMessageFromError } from '@/utils/error-util'
import { PaymentMethodError } from './payment-method-error'
import { required, validationContext } from 'validx'
import { tap } from 'rxjs'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'

/**
 * Form for paying with a new card.
 */
export class CardPaymentMethodFormViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Full name on the card.
   * Default to the current member's display name.
   */
  @observable accessor fullName: string

  /**
   * Country code.
   */
  @observable accessor country = 'US'

  /**
   * Zip code.
   */
  @observable accessor zipCode = ''

  /**
   * The Stripe card element, needed to create a token.
   */
  card: StripeCardElement | null = null

  /**
   * Validation.
   */
  readonly validation

  constructor(
    private readonly _member: Member,
    private readonly _onSubmit: () => Promise<void>,
    private readonly _addCardBillingMethod: AddCardBillingMethodFn,
    readonly stripe: Stripe | null,
    private readonly _isExistingMethodSelected: () => boolean,
  ) {
    this.fullName = this._member.displayName

    this.validation = validationContext(this, {
      fullName: [required('Name is required')],
      country: [required('Country is required')],
      zipCode: [required('Zip code is required')],
    })

    this._clearValidationOnChange('fullName')
    this._clearValidationOnChange('country')
    this._clearValidationOnChange('zipCode')
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Whether the form is disabled, because an existing payment method is
   * selected or a payment is pending.
   */
  @computed
  get formDisabled() {
    return this._isExistingMethodSelected() || this.pay.pending
  }

  /**
   * Submits the job with the selected payment method
   */
  pay = task.resolved(async () => {
    return await this._onSubmit()
  })

  /**
   * Sets the Stripe card element.
   */
  @action.bound
  setCard(card: StripeCardElement) {
    this.card = card
  }

  /**
   * Shows the card element's own validation error.
   */
  @action.bound
  handleCardChange(event: StripeElementChangeEvent) {
    this.validation.clearErrors('card')
    const error = event.error?.message
    if (error) {
      this.validation.addErrors({ card: [error] })
    }
  }

  /**
   * Sets the country.
   */
  @action.bound
  setCountry(value: string) {
    this.country = value
  }

  /**
   * Sets up automatic clearing of validation errors when the field changes.
   */
  private _clearValidationOnChange(field: keyof this) {
    this._lifecycle.manage(
      toStream(() => this[field]).pipe(
        tap(() => this.validation.clearErrors(field as string)),
      ),
    )
  }

  /**
   * Makeshift way of figuring out what the server error pertains to,
   * and assign the error to the right field in the validation context.
   *
   * @param err
   * @private
   */
  @action.bound
  private _maybeSniffMeaningfulInfoFromError(err: unknown) {
    const message = extractMessageFromError(err)
    if (/zip code/i.test(message)) {
      this.validation.addErrors({ zipCode: [message] })
      return true
    }

    if (/card/i.test(message) || err instanceof PaymentRequired) {
      this.validation.addErrors({ card: [message] })
      return true
    }

    return false
  }

  /**
   * Creates a payment method with the given card info.
   *
   * @throws {PaymentMethodError} when the form or card is not usable.
   */
  @action.bound
  async savePaymentMethod(): Promise<BillingMethod> {
    // Not `reset()`, which would also drop the error the card element reported.
    this.validation.clearErrors('fullName')
    this.validation.clearErrors('country')
    this.validation.clearErrors('zipCode')
    if (!this.validation.validate().isValid) {
      throw new PaymentMethodError(
        'You must select an existing payment method or fill the form to add a new one.',
      )
    }

    if (!this.stripe || !this.card) {
      throw new PaymentMethodError('Stripe was not loaded.')
    }

    const result = await this.stripe.createToken(this.card, {
      name: this.fullName,
      address_country: this.country,
      address_zip: this.zipCode,
    })

    if (result.error) {
      const message =
        result.error.message || 'There was a problem with your card.'
      runInAction(() => {
        this.validation.addErrors({ card: [message] })
      })
      throw new PaymentMethodError('There was a problem with your card.')
    }

    try {
      return await this._addCardBillingMethod(
        this._member,
        null,
        Id.assert(result.token?.id),
      )
    } catch (err) {
      if (this._maybeSniffMeaningfulInfoFromError(err)) {
        throw new PaymentMethodError(extractMessageFromError(err))
      }
      throw err
    }
  }
}
